import {
  CustomAttributeEntityType,
  FieldDataType,
  FieldConstraintObjectType,
} from '@rematter/frontend-lib-api-client';
import type { Atom } from 'jotai';
import { atom } from 'jotai';
import debounce from 'lodash/debounce';
import groupBy from 'lodash/groupBy';
import merge from 'lodash/merge';
import orderBy from 'lodash/orderBy';

import { customAttributeCreateMutationAtom } from '../_graphql/mutationCustomAttributeCreate';
import { customAttributeDeleteMutationAtom } from '../_graphql/mutationCustomAttributeDelete';
import { customAttributeUpdateMutationAtom } from '../_graphql/mutationCustomAttributeUpdate';
import { customAttributesQueryAtom } from '../_graphql/queryCustomAttributes';

import { CustomAttributeRecord } from '~/shared/components/custom-attributes/types';
import type { QueryAtomReadValue } from '~/shared/providers/api/createQueryAtomFactory';
import { LocalChange } from '~/shared/types/state';

type GroupedCustomAttributesData = Partial<
  Record<CustomAttributeEntityType, CustomAttributeRecord[]>
>;

type CustomAttributeForm = {
  name?: string;
  dataType?: FieldDataType;
  minLength?: number;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
  multipleValues?: boolean;
  required?: boolean;
  unique?: boolean;
  selectOptions?: string[];
  objectType?: FieldConstraintObjectType;
};

// 🟣🟣🟣 PARAMS
export const $customAttributeId = atom<string | undefined>(undefined);
export const $entityType = atom<CustomAttributeEntityType | undefined>(
  undefined,
);

// 🔘🔘🔘 PLAIN ATOMS
const $localChanges = atom<LocalChange<CustomAttributeRecord>[]>([]);
export const $showCreationDialog = atom(false);
export const $customAttributeForm = atom<CustomAttributeForm>({});

// 🟢🟢🟢 COMPUTED DATA
export const $customAttributeFormConstraints = atom((get) => {
  const formData = get($customAttributeForm);

  const dataType = formData.dataType;

  const canHaveLengthConstraint =
    dataType === 'Text' || dataType === 'MultilineText';

  const canHaveMinMaxValueConstraint =
    dataType === 'Number' || dataType === 'Money';

  const canHaveUniqueConstraint =
    dataType === 'Text' || dataType === 'UUID' || dataType === 'ObjectRef';

  const canHaveObjectType = dataType === 'ObjectRef';
  const canHaveSelectOptions = dataType === 'Select';

  const hasName = !!formData.name;

  const isSelect = dataType === 'Select';
  const hasSelectOptions =
    !!formData.selectOptions?.length &&
    formData.selectOptions.every((it) => !!it);

  const isObjectRef = formData.dataType === 'ObjectRef';
  const hasObjectType = !!formData.objectType;

  const canSubmitForm =
    hasName &&
    !!dataType &&
    (!isSelect || hasSelectOptions) &&
    (!isObjectRef || hasObjectType);

  return {
    canHaveLengthConstraint,
    canHaveMinMaxValueConstraint,
    canHaveUniqueConstraint,
    canHaveObjectType,
    canHaveSelectOptions,
    canSubmitForm,
  };
});

// 🟢🟢🟢 REMOTE DATA
const $remoteCustomAttributes = customAttributesQueryAtom();

// 🟢🟢🟢 COMPUTED DATA
export const $customAttributes: Atom<
  Omit<QueryAtomReadValue<CustomAttributeRecord[]>, 'data'> & {
    data: GroupedCustomAttributesData | undefined;
  }
> = atom((get) => {
  const remoteCustomAttributes = get($remoteCustomAttributes);
  const customAttributes = remoteCustomAttributes.data ?? [];
  const localChanges = get($localChanges);

  return {
    ...remoteCustomAttributes,
    data: groupBy(
      orderBy(
        customAttributes
          .map((attr) =>
            localChanges
              // filter local changes matching the attribute and registered after the attribute was updated
              .filter(
                (change) =>
                  change.where(attr) &&
                  change.changedAt > attr.updatedAt.toDate(),
              )
              // apply changes to the attribute
              .reduce((acc, change) => change.mutate(acc), attr),
          )
          .filter((attr) => !attr.isDeleted),
        [(it) => it.order, (it) => it.createdAt],
        ['asc', 'asc'],
      ),
      (it) => it.entityType,
    ),
  };
});

// 🟠🟠🟠 ACTION -> CREATE remote custom attribute
export const $createCustomAttribute = customAttributeCreateMutationAtom({
  // On success, refetch remote custom attributes
  onSuccess: (get) => get($remoteCustomAttributes).refetch(),
});

// 🟠🟠🟠 ACTION -> UPDATE remote custom attribute
export const $updateCustomAttribute = customAttributeUpdateMutationAtom({
  // Before mutation, update local changes
  before: (_get, set, { params, requestId }) =>
    set($localChanges, (changes) => [
      ...changes,
      {
        requestId,
        where: (record) => record.id === params.id,
        mutate: (record) => merge(structuredClone(record), params),
        changedAt: new Date(),
      },
    ]),

  // On error, undo local changes
  onError: (_get, set, { requestId }) =>
    set($localChanges, (changes) =>
      changes.filter((c) => c.requestId !== requestId),
    ),

  // On success, refetch remote custom attributes
  onSuccess: debounce((get) => get($remoteCustomAttributes).refetch(), 500),
});

// 🟠🟠🟠 ACTION -> DELETE remote custom attribute
export const $deleteCustomAttribute = customAttributeDeleteMutationAtom({
  // Before mutation, update local changes
  before: (_get, set, { params, requestId }) =>
    set($localChanges, (changes) => [
      ...changes,
      {
        requestId,
        where: (record) => record.id === params.id,
        mutate: (record) => ({ ...record, isDeleted: true }),
        changedAt: new Date(),
      },
    ]),

  // On error, undo local changes
  onError: (_get, set, { requestId }) =>
    set($localChanges, (changes) =>
      changes.filter((c) => c.requestId !== requestId),
    ),

  // On success, refetch remote custom attributes
  onSuccess: debounce((get) => get($remoteCustomAttributes).refetch(), 500),
});
